import React, { Fragment, useState } from 'react';
import { DatePicker, Input, OnboardingContainer } from '../../../components';
import {
  AddCustomQuestionButton,
  ButtonText,
  Content,
  Form,
  HeaderContainer,
  MoreQuestionsButton,
  QuestionnaireSection,
  SectionTitle,
  StyledButton,
  TitleContainer,
} from './UploadPersonalityPage.styles';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInfluencerRequestActionsPending,
  selectQuestionnaire,
} from '../../../store/slices/influencerRequest/slice';
import { Controller, useForm } from 'react-hook-form';
import { saveQuestionnaireAnswers } from '../../../store/slices/influencerRequest/asyncThunks';
import { setUserStatus } from '../../../store/slices/user/slice';
import { notifyError } from '../../../utils/notify';
import { Text20Bold } from '../../../components/utils/Text/Text.styles';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { format } from 'date-fns';

const UploadPersonalityPage = () => {
  const dispatch = useDispatch();
  const { saveQuestionnaireAnswers: saveQuestionnaireAnswersPending } = useSelector(
    selectInfluencerRequestActionsPending,
  );
  const questionnaire = useSelector(selectQuestionnaire);
  const [customQuestions, setCustomQuestions] = useState([{ question: '', answer: '' }]);

  const questionnaireFormSchema = yup
    .object()
    .shape({
      answers: yup.lazy(value => {
        const newEntries = Object.keys(value).reduce((acc, val) => {
          const [sectionIndex, questionIndex] = val.split('-');

          const question = questionnaire[sectionIndex].questions[questionIndex];

          if (question.required && question.type !== 'datepicker') {
            return {
              ...acc,
              [val]: yup.string().trim().required('Field is required'),
            };
          } else if (question.required && question.type === 'datepicker') {
            return {
              ...acc,
              [val]: yup
                .date()
                .required('Field is required')
                .max(
                  new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
                  'You must be at least 18 years old.',
                ),
            };
          } else {
            return acc;
          }
        }, {});

        return yup.object().shape(newEntries);
      }),
    })
    .required();

  const [showMore, setShowMore] = useState(false);

  const {
    register: questionnaireRegister,
    handleSubmit: questionnaireHandleSubmit,
    control,
    formState: { errors: questionnaireErrors },
  } = useForm({
    defaultValues: {
      answers: {},
    },
    delayError: 300,
    resolver: yupResolver(questionnaireFormSchema),
  });

  const onSubmitQuestionnaire = async data => {
    const questionnaireData = [];
    Object.entries(data.answers).forEach(([key, value]) => {
      const [sectionIndex, questionIndex] = key.split('-');
      const title = questionnaire[sectionIndex].title;
      const question = questionnaire[sectionIndex].questions[questionIndex];
      if (question.type === 'datepicker') {
        const dateOfBirth = new Date(value);
        const formattedDate = format(
          Date.UTC(dateOfBirth.getFullYear(), dateOfBirth.getMonth(), dateOfBirth.getDate()),
          'yyyy/MM/dd',
        );
        value = formattedDate;
      }
      questionnaireData.push({
        title,
        question,
        answer: value,
      });
    });

    for (const customQuestion of customQuestions) {
      if (customQuestion.question.trim()) {
        questionnaireData.push({
          title: 'Custom Questions',
          question: { text: customQuestion.question },
          answer: customQuestion.answer,
        });
      }
    }

    dispatch(saveQuestionnaireAnswers({ questionnaireData }))
      .unwrap()
      .then(({ userStatus }) => {
        dispatch(setUserStatus(userStatus));
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <OnboardingContainer>
      <Content>
        <HeaderContainer>
          <Text20Bold>Personality Questions</Text20Bold>
        </HeaderContainer>
        <Form onSubmit={questionnaireHandleSubmit(onSubmitQuestionnaire)}>
          {questionnaire.map((question, index) => {
            return (
              question.questions.some(q => q.required) &&
              !['AI Limits', 'Writing Style'].includes(question.title) && (
                <QuestionnaireSection key={index}>
                  <TitleContainer>
                    <SectionTitle>{question.title}</SectionTitle>
                  </TitleContainer>
                  {question.questions.map((q, i) => {
                    return (
                      (q.required || showMore) &&
                      (q.type === 'datepicker' ? (
                        <Controller
                          key={i}
                          name={`answers[${index}-${i}]`}
                          defaultValue={null}
                          control={control}
                          render={({ field }) => {
                            return (
                              <DatePicker
                                label={q.text}
                                placeholder="Your answer"
                                value={field.value}
                                onChange={field.onChange}
                                error={questionnaireErrors.answers?.[`${index}-${i}`]?.message}
                              />
                            );
                          }}
                        />
                      ) : (
                        <Input
                          key={i}
                          label={q.text}
                          type={q.type || 'textarea'}
                          placeholder="Your answer"
                          {...questionnaireRegister(`answers[${index}-${i}]`)}
                          error={questionnaireErrors.answers?.[`${index}-${i}`]?.message}
                        />
                      ))
                    );
                  })}
                </QuestionnaireSection>
              )
            );
          })}
          {showMore && (
            <>
              <QuestionnaireSection>
                <TitleContainer>
                  <SectionTitle>Custom Questions</SectionTitle>
                </TitleContainer>
                {customQuestions.map((q, i) => {
                  return (
                    <Fragment key={i}>
                      <Input
                        label="Question"
                        value={q.question}
                        onChange={e => {
                          const newQuestions = [...customQuestions];
                          newQuestions[i].question = e.target.value;
                          setCustomQuestions(newQuestions);
                        }}
                        placeholder="Enter your question"
                      />
                      <Input
                        label="Answer"
                        value={q.answer}
                        onChange={e => {
                          const newQuestions = [...customQuestions];
                          newQuestions[i].answer = e.target.value;
                          setCustomQuestions(newQuestions);
                        }}
                        type="textarea"
                        placeholder="Enter your Answer"
                      />
                    </Fragment>
                  );
                })}
              </QuestionnaireSection>
              <AddCustomQuestionButton
                type="button"
                onClick={() => setCustomQuestions(prev => [...prev, { question: '', answer: '' }])}>
                <ButtonText>Add Custom Question</ButtonText>
                <PlusIcon />
              </AddCustomQuestionButton>
            </>
          )}

          <MoreQuestionsButton
            type="button"
            title={showMore ? 'Show Less' : 'More Questions'}
            onClick={() => setShowMore(!showMore)}
          />
          <StyledButton title="Continue" isLoading={saveQuestionnaireAnswersPending} />
        </Form>
      </Content>
    </OnboardingContainer>
  );
};

export default UploadPersonalityPage;
